// apps/client/src/input/joystick.ts — floating joystick: appears under the left thumb (look-demo input.ts, made floating)
import { tuning } from '@whiskerton/content';
const I = tuning.input;
export function stickVector(origin: { x: number; y: number }, p: { x: number; y: number }, radius: number): { x: number; y: number; mag: number } {
  let dx = p.x - origin.x, dy = p.y - origin.y;
  const l = Math.hypot(dx, dy);
  if (l > radius) { dx *= radius / l; dy *= radius / l; }
  const x = dx / radius, y = dy / radius;
  return { x, y, mag: Math.min(1, Math.hypot(x, y)) };
}
export const inJoystickZone = (clientX: number, width: number): boolean => clientX < width * I.joystickZone;
export class FloatingJoystick {
  private readonly el: HTMLElement;
  private readonly base: HTMLDivElement;
  private readonly knob: HTMLDivElement;
  private id: number | null = null;
  private origin = { x: 0, y: 0 };
  private v = { x: 0, y: 0, mag: 0 };
  constructor(el: HTMLElement) {
    this.el = el;
    this.base = document.createElement('div');
    this.base.style.cssText = `position:fixed;width:${I.joystickRadiusPx * 2}px;height:${I.joystickRadiusPx * 2}px;border-radius:50%;background:rgba(255,251,241,.35);box-shadow:0 10px 22px -12px rgba(36,50,40,.55);pointer-events:none;display:none;transform:translate(-50%,-50%);`;
    this.knob = document.createElement('div');
    this.knob.style.cssText = 'position:absolute;left:50%;top:50%;width:44px;height:44px;margin:-22px;border-radius:50%;background:#FFFBF1;box-shadow:0 6px 14px -6px rgba(36,50,40,.6);';
    this.base.append(this.knob);
    document.body.append(this.base);
    el.addEventListener('pointerdown', this.down);
    window.addEventListener('pointermove', this.move);
    window.addEventListener('pointerup', this.up);
    window.addEventListener('pointercancel', this.up);
  }
  get active(): boolean { return this.id !== null; }
  vector(): { x: number; y: number; running: boolean } { return { x: this.v.x, y: this.v.y, running: this.v.mag > I.runDeflection }; }
  dispose(): void {
    this.el.removeEventListener('pointerdown', this.down);
    window.removeEventListener('pointermove', this.move); window.removeEventListener('pointerup', this.up); window.removeEventListener('pointercancel', this.up);
    this.base.remove();
  }
  private readonly down = (e: PointerEvent) => {
    // Touch only: a mouse or a pen clicks to walk anywhere on the world, the left zone too (§8.4 "Desktop: … click to tap"), and a
    // desktop moves with the keys.
    if (this.id !== null || e.pointerType !== 'touch' || !inJoystickZone(e.clientX, innerWidth) || (e.target as HTMLElement).closest('.hit')) return;
    this.id = e.pointerId; this.origin = { x: e.clientX, y: e.clientY };
    Object.assign(this.base.style, { display: 'block', left: `${e.clientX}px`, top: `${e.clientY}px` });
  };
  private readonly move = (e: PointerEvent) => {
    if (e.pointerId !== this.id) return;
    this.v = stickVector(this.origin, { x: e.clientX, y: e.clientY }, I.joystickRadiusPx);
    this.knob.style.transform = `translate(${this.v.x * I.joystickRadiusPx}px, ${this.v.y * I.joystickRadiusPx}px)`;
  };
  private readonly up = (e: PointerEvent) => {
    if (e.pointerId !== this.id) return;
    this.id = null; this.v = { x: 0, y: 0, mag: 0 };
    this.base.style.display = 'none'; this.knob.style.transform = '';
  };
}
